import { useEffect, useState } from "react";
import { Skull } from "lucide-react";
import { Sounds } from "../lib/sound";

/**
 * BossIntro
 *
 * 1.4-second cinematic overlay that appears when a boss level loads.
 * Plays a deep ominous sting through the existing Sounds module, then
 * fades out so the pilot can read the HP bar and start playing.
 *
 * Self-dismisses after the animation completes; renders nothing once
 * dismissed. Pass a stable `bossKey` (e.g. globalLevel + bossName) so the
 * overlay re-appears when the pilot navigates between bosses.
 */
export default function BossIntro({ bossName, sectorName, bossKey, killCount }) {
  const [visible, setVisible] = useState(true);
  const [phase, setPhase] = useState("enter"); // enter | sustain | exit

  useEffect(() => {
    setVisible(true);
    setPhase("enter");
    // Boss approach sting: mirror the bossDefeat() composition but inverted —
    // ascending impact instead of descending wail.
    try { Sounds.warp(); } catch (_) { /* ignore */ }
    const t1 = setTimeout(() => setPhase("sustain"), 350);
    const t2 = setTimeout(() => setPhase("exit"), 950);
    const t3 = setTimeout(() => setVisible(false), 1400);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [bossKey]);

  if (!visible) return null;

  // Map phase → opacity + scale so the title swells in, holds, fades out.
  const opacity = phase === "exit" ? 0 : 1;
  const scale =
    phase === "enter"   ? 0.85 :
    phase === "sustain" ? 1 :
    1.05;

  const isFirstBlood = killCount === 0;
  // Accent color shifts to gold for first-blood state — the entire chrome
  // (skulls, divider, name shadow) follows so the pilot reads "this is
  // historically significant" before the killCount line resolves.
  const accent = isFirstBlood ? "#ffd700" : "#ff007f";
  const accentRgb = isFirstBlood ? "255,215,0" : "255,0,127";

  return (
    <div
      className="fixed inset-0 z-[120] pointer-events-none flex items-center justify-center"
      data-testid="boss-intro-overlay"
      style={{
        background: phase === "exit" ? "rgba(0,0,0,0)" : "rgba(0,0,0,0.55)",
        backdropFilter: phase === "exit" ? "none" : "blur(6px)",
        WebkitBackdropFilter: phase === "exit" ? "none" : "blur(6px)",
        transition: "background 0.45s ease-out, backdrop-filter 0.45s ease-out",
      }}
    >
      {/* First-blood electric pulse — only fires when killCount is exactly 0,
          i.e. nobody on the entire server has cleared this boss yet. Twin
          radial gold halos pulse outward on a 1.4s cycle, framing the boss
          name with "this is a historic kill" energy. */}
      {isFirstBlood && (
        <>
          <div
            className="absolute inset-0 pointer-events-none"
            data-testid="boss-intro-first-blood-pulse"
            style={{
              background: `radial-gradient(circle at 50% 50%, rgba(${accentRgb},0.22) 0%, transparent 55%)`,
              animation: "bossFirstBloodPulse 1.4s ease-out infinite",
            }}
          />
          <style>{`
            @keyframes bossFirstBloodPulse {
              0%   { opacity: 0.0; transform: scale(0.85); }
              45%  { opacity: 1.0; transform: scale(1.05); }
              100% { opacity: 0.0; transform: scale(1.20); }
            }
            @keyframes bossFirstBloodGlitch {
              0%, 100% { transform: translateX(0); }
              20% { transform: translateX(-2px); }
              40% { transform: translateX(2px); }
              60% { transform: translateX(-1px); }
              80% { transform: translateX(1px); }
            }
          `}</style>
        </>
      )}
      <div
        className="text-center px-8 relative"
        style={{
          opacity,
          transform: `scale(${scale})`,
          transition: "opacity 0.45s ease-out, transform 0.6s cubic-bezier(0.2, 0.9, 0.3, 1)",
        }}
      >
        <div className="flex items-center justify-center gap-3 mb-2">
          <div className="h-px flex-1 max-w-[120px]"
            style={{ background: `linear-gradient(90deg, transparent, ${accent})` }} />
          <Skull className="w-6 h-6"
            style={{ color: accent, filter: `drop-shadow(0 0 10px rgba(${accentRgb},0.8))` }} />
          <div className="font-mono-cosmic text-[11px] tracking-[0.55em] uppercase" style={{ color: accent }}>
            {isFirstBlood ? "First Blood Approaches" : "Boss Approaches"}
          </div>
          <Skull className="w-6 h-6"
            style={{ color: accent, filter: `drop-shadow(0 0 10px rgba(${accentRgb},0.8))` }} />
          <div className="h-px flex-1 max-w-[120px]"
            style={{ background: `linear-gradient(270deg, transparent, ${accent})` }} />
        </div>
        <div
          className="font-heading font-black uppercase tracking-tight text-4xl sm:text-6xl text-white leading-none mb-2"
          style={{
            textShadow: `0 0 28px rgba(${accentRgb},0.85), 0 0 42px rgba(${accentRgb},0.4)`,
            animation: isFirstBlood && phase === "sustain"
              ? "bossFirstBloodGlitch 0.7s ease-in-out 1"
              : undefined,
          }}
          data-testid="boss-intro-name"
        >
          {bossName}
        </div>
        {sectorName && (
          <div className="font-mono-cosmic text-[10px] sm:text-xs tracking-[0.45em] uppercase text-white/60">
            Sector · {sectorName}
          </div>
        )}
        {killCount != null && killCount > 0 && (
          <div
            className="font-mono-cosmic text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#ff007f]/80 mt-3"
            data-testid="boss-intro-kills"
            style={{ textShadow: "0 0 10px rgba(255,0,127,0.6)" }}
          >
            Defeated by {killCount.toLocaleString()} pilot{killCount === 1 ? "" : "s"}
          </div>
        )}
        {isFirstBlood && (
          <div
            className="font-mono-cosmic text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#ffd700]/90 mt-3"
            data-testid="boss-intro-first-blood"
            style={{ textShadow: "0 0 10px rgba(255,215,0,0.6)" }}
          >
            ◇ First Blood Available ◇
          </div>
        )}
      </div>
    </div>
  );
}
